import { useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/pages/home/components/MovieCard';
import { movies } from '@/mocks/movies';

const categoryNames: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  variety: '综艺',
  anime: '动漫',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const movie = movies.find((m) => m.id === Number(id));

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-900 mb-2">未找到该影片</h2>
            <p className="text-foreground-500 mb-6">该影片可能已下架或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const categoryName = categoryNames[movie.category] || movie.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: movie.title,
    description: movie.description,
    datePublished: movie.releaseDate,
    author: {
      '@type': 'Organization',
      name: '极速影视',
    },
    publisher: {
      '@type': 'Organization',
      name: '极速影视',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${movie.category}` },
                { label: movie.title },
              ]}
            />
          </div>

          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-10">
              <div className="lg:w-[350px] flex-shrink-0">
                <div className="sticky top-24 rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={movie.image}
                    alt={movie.title}
                    title={`${movie.title} - 极速影视`}
                    className="w-full aspect-[16/10] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-2 mb-3">
                  {movie.isNew && (
                    <span className="px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
                      最新上线
                    </span>
                  )}
                  {movie.isHot && (
                    <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
                      热播推荐
                    </span>
                  )}
                  <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                    {movie.subcategory}
                  </span>
                </div>

                <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-6 text-sm">
                  <span className="flex items-center gap-1 text-accent-500 font-semibold text-lg">
                    <i className="ri-star-fill"></i>
                    {movie.rating}
                  </span>
                  <span className="px-2 py-0.5 rounded bg-background-100 text-foreground-600">{movie.year}</span>
                  <span className="px-2 py-0.5 rounded bg-background-100 text-foreground-600">{movie.region}</span>
                  <span className="px-2 py-0.5 rounded bg-background-100 text-foreground-600">{movie.duration}</span>
                  {movie.episodes > 1 && (
                    <span className="px-2 py-0.5 rounded bg-background-100 text-foreground-600">
                      共{movie.episodes}集
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 p-5 rounded-xl bg-background-100">
                  <div>
                    <span className="text-xs text-foreground-500">导演</span>
                    <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.director}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">主演</span>
                    <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.actors.join(' / ')}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">上映日期</span>
                    <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.releaseDate}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">类型</span>
                    <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.subcategory}</p>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-lg font-bold text-foreground-900 font-heading mb-3">剧情简介</h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">{movie.description}</p>
                </div>

                <Link
                  to={`/article/${movie.id}`}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line text-xl"></i>
                  立即观看
                </Link>
              </div>
            </div>
          </div>

          {relatedMovies.length > 0 && (
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16">
              <div className="mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                  相关推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">猜您还喜欢这些</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          )}
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}